/**
 * FileRoleBadge Component
 *
 * Displays the detected role of a file with confidence indicator.
 *
 * @epic DC-573
 * @task Phase 3 UI Components
 */

import React from 'react';

export type FileRole =
  | 'primary_data'
  | 'data_dictionary'
  | 'metadata'
  | 'qc_report'
  | 'crosswalk'
  | 'license'
  | 'reference'
  | 'documentation'
  | 'unknown';

export type RoleAction = 'auto_assign' | 'auto_with_review' | 'suggest' | 'require_user';

export interface FileRoleBadgeProps {
  /** The detected role */
  role: FileRole;
  /** Confidence score (0-1) */
  confidence?: number;
  /** Action recommendation based on confidence */
  action?: RoleAction;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Whether to show confidence indicator */
  showConfidence?: boolean;
  /** Whether the badge is clickable */
  onClick?: () => void;
}

/** Role display configuration */
const ROLE_CONFIG: Record<FileRole, { label: string; icon: string; color: string; bgColor: string }> = {
  primary_data: {
    label: 'Primary Data',
    icon: '📊',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  data_dictionary: {
    label: 'Dictionary',
    icon: '📖',
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
  },
  metadata: {
    label: 'Metadata',
    icon: '📋',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  qc_report: {
    label: 'QC Report',
    icon: '✅',
    color: 'text-teal-700',
    bgColor: 'bg-teal-100',
  },
  crosswalk: {
    label: 'Crosswalk',
    icon: '🔗',
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
  },
  license: {
    label: 'License',
    icon: '📄',
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  },
  reference: {
    label: 'Reference',
    icon: '📚',
    color: 'text-indigo-700',
    bgColor: 'bg-indigo-100',
  },
  documentation: {
    label: 'Documentation',
    icon: '📝',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-100',
  },
  unknown: {
    label: 'Unknown',
    icon: '❓',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
  },
};

/** Size configurations */
const SIZE_CONFIG = {
  sm: 'text-xs px-2 py-0.5',
  md: 'text-sm px-2.5 py-1',
  lg: 'text-base px-3 py-1.5',
};

/**
 * Get confidence level label
 */
function getConfidenceLabel(confidence: number): { label: string; color: string } {
  if (confidence >= 0.9) return { label: 'High', color: 'text-green-600' };
  if (confidence >= 0.7) return { label: 'Good', color: 'text-blue-600' };
  if (confidence >= 0.5) return { label: 'Medium', color: 'text-yellow-600' };
  return { label: 'Low', color: 'text-red-600' };
}

/**
 * FileRoleBadge displays the detected role of a file.
 */
export function FileRoleBadge({
  role,
  confidence,
  action,
  size = 'md',
  showConfidence = true,
  onClick,
}: FileRoleBadgeProps) {
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.unknown;
  const sizeClass = SIZE_CONFIG[size];
  const isClickable = !!onClick;

  // Determine if review is needed
  const needsReview = action === 'suggest' || action === 'require_user';

  return (
    <span
      className={`
        inline-flex items-center gap-1.5 rounded-full font-medium
        ${config.bgColor} ${config.color} ${sizeClass}
        ${isClickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}
        ${needsReview ? 'ring-2 ring-yellow-400 ring-offset-1' : ''}
      `}
      onClick={onClick}
      title={`${config.label}${confidence !== undefined ? ` (${(confidence * 100).toFixed(0)}% confidence)` : ''}`}
    >
      <span>{config.icon}</span>
      <span>{config.label}</span>

      {showConfidence && confidence !== undefined && (
        <span className={`text-xs ${getConfidenceLabel(confidence).color}`}>
          ({(confidence * 100).toFixed(0)}%)
        </span>
      )}

      {needsReview && (
        <span className="text-yellow-600" title="Click to review/change role">
          ⚠
        </span>
      )}
    </span>
  );
}

/**
 * Compact version showing just the icon
 */
export function FileRoleIcon({ role, size = 'md' }: { role: FileRole; size?: 'sm' | 'md' | 'lg' }) {
  const config = ROLE_CONFIG[role] || ROLE_CONFIG.unknown;
  const sizeClass = {
    sm: 'w-5 h-5 text-xs',
    md: 'w-6 h-6 text-sm',
    lg: 'w-8 h-8 text-base',
  }[size];

  return (
    <span
      className={`
        inline-flex items-center justify-center rounded-full
        ${config.bgColor} ${sizeClass}
      `}
      title={config.label}
    >
      {config.icon}
    </span>
  );
}

export default FileRoleBadge;
